/** Shared in-flight prompt so VAPID + FCM never double-ask. */
let permissionInFlight: Promise<NotificationPermission> | null = null
let gestureListenersAttached = false

const PROMPT_TIMEOUT_MS = 12_000

function readPermission(): NotificationPermission {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'denied'
  return Notification.permission
}

/**
 * Chrome can leave `requestPermission()` pending forever if the dialog is
 * suppressed. Never block the UI on that — fall back to current permission.
 */
function withPermissionTimeout(
  pending: Promise<NotificationPermission>,
): Promise<NotificationPermission> {
  return new Promise((resolve) => {
    let settled = false
    const finish = (value: NotificationPermission) => {
      if (settled) return
      settled = true
      resolve(value)
    }

    const timer = window.setTimeout(() => {
      finish(readPermission())
    }, PROMPT_TIMEOUT_MS)

    pending.then(
      (value) => {
        window.clearTimeout(timer)
        finish(value)
      },
      () => {
        window.clearTimeout(timer)
        finish(readPermission())
      },
    )
  })
}

/**
 * Returns current Notification permission.
 * If `default`, requests once (shared across push + FCM callers).
 * Timed out prompts fall back to the current permission so callers never hang.
 */
export async function ensureNotificationPermission(): Promise<NotificationPermission> {
  const current = readPermission()
  if (current !== 'default') return current

  if (!permissionInFlight) {
    permissionInFlight = withPermissionTimeout(Notification.requestPermission()).finally(() => {
      permissionInFlight = null
    })
  }
  return permissionInFlight
}

/**
 * Ask for notification permission on the first user gesture (Chrome/Edge need this
 * for Allow/Block). Does not render any in-app Enable/Later UI.
 */
export function primeNotificationPermissionOnGesture(): () => void {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return () => undefined
  }
  if (Notification.permission !== 'default') return () => undefined

  if (gestureListenersAttached) return () => undefined
  gestureListenersAttached = true

  const ask = () => {
    // Fire-and-forget; timeout inside ensureNotificationPermission prevents hangs.
    void ensureNotificationPermission()
  }

  document.addEventListener('pointerdown', ask, { once: true, capture: true })
  document.addEventListener('keydown', ask, { once: true, capture: true })

  return () => {
    document.removeEventListener('pointerdown', ask, true)
    document.removeEventListener('keydown', ask, true)
    gestureListenersAttached = false
  }
}
